import React from 'react';
import { navigate } from 'gatsby';
import styled from 'styled-components';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import TextLinkWithIcon from '@components/elements/text-link-with-icon';
import mixins from '@styles/mixins';
import media from '@styles/media';
import theme from '@styles/theme';
const { colors, hyperlinks } = theme;

const StyledCard = styled.div`
  width: 100%;
  padding: 0px 0px;
  box-sizing: border-box;

  &.wide-card {
    max-width: 100%;
    ${media.bigDesktop`max-width: 1110px;`};
    ${media.desktop`max-width: 691px;`};
    ${media.thone`width: 100%; max-width: unset;`};
  }

  &.normal-card {
    max-width: 100%;
    ${media.thone`width: 100%; max-width: unset;`};
  }

  &.small-card {
    max-width: 400px;
    ${media.giant`max-width: 350px;`};
    ${media.desktop`max-width: 210px;`};
    ${media.thone`width: 100%; max-width: unset;`};
  }

  header {
    padding: 20px 0 12px;

    ${media.giant`padding: 5px 0 10px;`};
  }
`;

const CoverImg = styled.div`
  width: 100%;
  background-color: #d8d8d8;
  position: relative;
  box-shadow: rgb(0, 0, 0, 0.18) 0px 4px 28px;

  .cover-image {
    width: 100%;
    height: 100%;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }

  &.wide {
    height: 600px;
    ${media.bigGiant`height: 511px;`};
    ${media.desktop`height: 346px;`};
    ${media.thone`height: 282x;`};
  }

  &.normal {
    height: 584px;
    ${media.bigGiant`height: 456px;`};
    ${media.giant`height: 408px;`};
    ${media.desktop`height: 299px;`};
    ${media.tablet`height: 240px;`};
    ${media.phone`height: 200px;`};
  }

  &.small {
    height: 400px;
    ${media.giant`height: 350px;`};
    ${media.desktop`height: 212px;`};
    ${media.phone`height: 140px;`};
  }
`;

const StyledTitleWrapp = styled.div`
  top: 25px;
  padding: 12px 50px 10px 12px;
  position: absolute;
  background: linear-gradient(271.86deg, rgba(82, 21, 252, 0.33) 4.94%, #280886 43.73%), #280886;
  clip-path: polygon(0 0, 100% 0, 90% 100%, 0% 100%);
  z-index: 9;
  font-size: 28px;
  font-weight: 900;
  color: ${colors.white};
  ${media.bigGiant`
    top: 25px;
    padding: 8px 35px 7px 8px; 
    font-size: 18px;
  `};
  ${media.tablet`
    top: 25px;
    padding: 10px 35px 8px 10px; 
    font-size: 22px;
  `};
  ${media.thone`padding-right: 45px;`};
`;

const StyledTextContent = styled.div`
  padding: 57px 0 10px;
  ${media.giant`padding: 47px 0 10px;`};
  ${media.desktop`padding: 30px 0 10px;`};
`;

const StyledSubTitle = styled.div`
  color: ${colors.darkPurple};
  text-transform: uppercase;
  ${({ type }) => (type === 'wide' ? mixins.subtitle32 : mixins.subtitle20)};
`;

const StyledCardTitle = styled.div`
  color: ${colors.black};
  text-transform: uppercase;
  padding-top: 0 !important;
  ${({ type }) => (type === 'wide' ? mixins.header42 : mixins.header32_big)};
`;

const StyledTextLink = styled(TextLinkWithIcon)`
  padding-top: 20px;
  ${({ type }) => (type === 'wide' ? hyperlinks.bold24 : mixins.hyperlinkbold24_medium)};
  ${media.desktop`
    ${({ type }) =>
      type === 'wide' &&
      `
      a { margin: auto; }
    `};
  `};
`;

const StyledCaption = styled.div`
  word-break: break-word;
  padding: 0.6rem 1rem 0 0;
  font-size: 16px;
  color: #73737d;
  text-overflow: ellipsis;
  overflow-wrap: normal;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  display: -webkit-box;
  white-space: normal;
  overflow: hidden;

  ${({ viewMode }) =>
    !viewMode &&
    `
    display: none;
  `}

`;

const StyledDescription = styled.div`
  padding: 35px 0 15px;
  text-transform: initial;
  ${mixins.text32_24_20_16_20};
  ${media.giant`padding: 20px 0 0;`}
  ${media.desktop`padding: 35px 0 15px;`}
`;

const StyledWideCard = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0px 30px;

  ${media.desktop`
    &>div:nth-child(1) {
      order: 2;
      grid-column: 1 / -1;
    }
    &>div:nth-child(2) {
      order: 1;
      grid-column: 1 / -1;
    }
  `};
`;

const InsightWideCard = ({ data }) => {
  const { url, type, title, excerpt, category, featuredArticle } = data || {};
  const image = getImage(data?.coverImage)

  return (
    <StyledWideCard className="overlay-animation" onClick={() => navigate(`/${url}`)}>
      <StyledTextContent>
        <StyledSubTitle type="wide">{category}</StyledSubTitle>
        <StyledCardTitle type="wide">{title}</StyledCardTitle>
        {excerpt && <StyledDescription>{excerpt?.excerpt}</StyledDescription>}
        <StyledTextLink url={`/${url}`} color="darkPurple" type="wide" />
      </StyledTextContent>
      <CoverImg className={`image-content card-image wide`}>
        {image && <GatsbyImage image={image} className="cover-image" alt={data?.coverImage?.title || 'insight-image'} />}
        {featuredArticle && <StyledTitleWrapp>{type}</StyledTitleWrapp>}
      </CoverImg>
    </StyledWideCard>
  );
};

const InsightPreviewCard = ({ data = {}, type, listView }) => {
  const image = getImage(data?.coverImage)

  return (
    <StyledCard
      className={`${type}-card overlay-animation`}
      onClick={() => navigate(`/${data.url}`)}>
      <CoverImg className={`${type} image-content card-image`}>
        {image && <GatsbyImage image={image} className="cover-image" alt={data.coverImage?.title || "insight preview image"} />}
        {data?.featuredArticle && <StyledTitleWrapp>{data?.type}</StyledTitleWrapp>}
      </CoverImg>
      <header>
        <StyledSubTitle type={type}>{data.category}</StyledSubTitle>
        <StyledCardTitle type={type}>{data.title}</StyledCardTitle>
        <StyledCaption type={type} viewMode={listView}>
          {data.excerpt?.excerpt}
        </StyledCaption>

        <StyledTextLink url={`/${data.url}`} color="darkPurple" type={type} />
      </header>
    </StyledCard>
  );
};

export { InsightPreviewCard, InsightWideCard };
